<template>
  <div
    :ref="'card'"
    class="tf-carousel-card position-relative bg-primary-white border-grey-300"
    :class="[screenType === 'mobile' ? 'my-2 p-3' : 'p-4 mx-2']"
    @click.stop="state.popupOpen = true"
  >
    <div ref="cardInnerWrapper" class="card-inner-wrapper overflow-hidden position-relative" style="max-height: 100%;">
      <div class="d-flex body-strong text-grey-900 justify-content-between">
        <div>
          <span v-if="formattedSurname">{{ formattedSurname }}</span>
          <span>{{ " " }}</span>
          <span v-if="review.authorName">{{ review.authorName }}</span>
          
          
        </div>
        <div class="d-flex flex-row align-items-center justify-content-center">
          <span class="me-1">{{ formattedRatingByLocale }}/5</span>
          <svg class="icon text-primary-blue" width="16" height="16"
            style="position: relative; top: -2px;">
            <use href="#___icon___star-full"></use>
          </svg>
        </div>
      </div>
      <div class="mb-3 body text-grey-600">
        <span>{{ getCreatedDatePretty(review.creationDate) }}</span>
      </div>
      <div>
        <div class="body text-grey-900">
          <p>{{ displayedCustomerComment }}</p>
        </div>
      </div>
    </div>

    <div class="show-more-btn position-absolute bg-primary-white" style="bottom: 1.5rem; right: 1.5rem; box-shadow: white -10px 5px 10px 6px;">
      <span class="cursor-pointer link-small ps-1">{{ content.reviews_seeMore_popup }}</span>
    </div>

    <PopupBase :open="state.popupOpen" :overlayMaxOpacity="0.8" @closePopup="state.popupOpen = false">
      <ReviewPopup :open="state.popupOpen" :review="review" :ratings="ratings" @closePopup="state.popupOpen = false" />
    </PopupBase>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import PopupBase from '@/vue/common/popupBase.vue'
import ReviewPopup from '@/vue/reviews-translation/components/popups/reviewPopup.vue'
import ratingOrder from '@/config/ratingOrder'
import { companyLangToLocale } from '@/config/localeConfig'
import { getOverallReviewRatingValue } from '@/vue/reviews-translation/utils/overallReviewRating.js'

const props = defineProps({
  review: { type: Object, default: () => ({}) },
  cardWidth: String
})

const store = useStore()
const content = computed(() => store.state.content)
const screenType = computed(() => store.state.screenType)
const language = computed(() => store.state.language)
// console.log('Review component initialized with props:', screenType.value, language.value)

const state = ref({
  popupOpen: false,
  overflowing: false
})

const cardInnerWrapper = ref(null)

const ratings = computed(() =>
  ratingOrder
    .map((ratingName) => {
      const key = ratingName.replace('Average', '')
      return { name: ratingName, rating: props.review[key] }
    })
    .filter((r) => r.rating != null)
)

const formattedRatingByLocale = computed(() => {
  try {
    const overall = getOverallReviewRatingValue(props.review) ?? 0
    const raw = overall.toFixed(1).replace(/\.0$/, '') || '0'
    return language.value.startsWith('fr') ? raw.replace('.', ',') : raw
  } catch {
    return '0'
  }
})

function getCreatedDatePretty(dateStr) {
  const dateObj = new Date(dateStr)
  const options = { year: 'numeric', month: 'long', day: 'numeric' }

  let locale = language.value

  if (locale === 'fr-FR-CSE') {
    const match = document.cookie.match('(^|;)\\s*companyLang\\s*=\\s*([^;]+)')
    const shortLang = match?.[2]?.trim()
    locale = companyLangToLocale.get(shortLang) || 'en-US'
  }

  try {
    return dateObj.toLocaleString(locale, options)
  } catch {
    return dateObj.toLocaleString('en-US', options)
  }
}

function isCardOverflowing(el) {
  const prev = el.style.overflow
  el.style.overflow = 'hidden'
  const result = el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight
  el.style.overflow = prev
  return result
}

watch(() => props.cardWidth, () => {
  if (cardInnerWrapper.value) {
    state.value.overflowing = isCardOverflowing(cardInnerWrapper.value)
  }
})

onMounted(() => {
  if (cardInnerWrapper.value) {
    state.value.overflowing = isCardOverflowing(cardInnerWrapper.value)
  }
})

const normalizeLang = (lang) => lang?.toLowerCase().slice(0, 2) || ''
const siteLang = computed(() => normalizeLang(language.value))

function parseLangComment(rawComment) {
  if (!rawComment || typeof rawComment !== 'string') return {}
  if (!rawComment.match(/\b\w{2}:/)) return { raw: rawComment }

  const parts = rawComment.split(/\s*\|\s*/)
  const result = {}
  for (const part of parts) {
    const match = part.match(/^(\w{2}):\s*(.+)$/s)
    if (match) {
      const lang = match[1].toLowerCase().slice(0, 2)
      result[lang] = match[2].trim()
    }
  }
  return result
}

const parsedCommentMap = computed(() => parseLangComment(props.review.customerComment))

const displayedCustomerComment = computed(() => {
  const map = parsedCommentMap.value
  const lang = siteLang.value
  if (map.raw) return map.raw
  return map[lang] || map[props.review.language?.toLowerCase()] || Object.values(map)[0] || ''
})

const formattedSurname = computed(() => {
  if (!props.review.authorSurname) return ''
  return props.review.authorSurname.charAt(0).toUpperCase() + '.'
})

</script>

<style scoped>
.tf-carousel-card {
  border-radius: var(--border-radius-card-secondary);
}

.show-more-btn {
  box-shadow: white -10px 5px 10px 6px;
  bottom: 1.5rem;
  right: 1.5rem;
}

.card-inner-wrapper {
  max-height: 100%;
}
</style>
